import { atom, useAtom } from "jotai";
import { UCLColors } from "./uclColors";
import { NameFieldValue } from "../Fields/types/fieldTypes";
import { hashString } from "../../utils/string";
import { atomFamily } from "jotai/utils";
import { Poline, PolineOptions } from "poline";

const highContrastNotTooDarkUclColors = Object.fromEntries(
  Object.entries(UCLColors).filter(
    ([key]) =>
      key !== "neutral-gray" &&
      key !== "silver" &&
      key !== "charcoal" &&
      key !== "platinum" &&
      key !== "steel-gray" &&
      key !== "cream"
  )
);

const uclColorsArray = Object.values(highContrastNotTooDarkUclColors);

// The use of jotai is actually unnecessary, since we should always be able to recompute the value.
// Still, we will keep it around for now.

interface ColorSwatch {
  swatch: string;
  poline?: Poline;
  colors: Record<string, string>;
}

const colorFamily = atomFamily((swatch: string) =>
  atom<ColorSwatch>({
    swatch,
    colors: {},
  })
);

export interface ColorsModule {
  // The name of this swatch
  swatch: string;

  // We promise to provide a stable color for a given id, within this swatch
  getColorForId: (id: string) => string;

  // We promise to provide a stable color for a given name, within this swatch
  getColorForName: (name?: NameFieldValue) => string;
}

export const useColors = (
  swatch: string = "default",
  polineOptions?: PolineOptions
): ColorsModule => {
  const [colorSwatch, setColorSwatch] = useAtom(colorFamily(swatch));

  const poline =
    colorSwatch.poline ??
    (polineOptions ? new Poline(polineOptions) : new Poline());

  if (polineOptions && !colorSwatch.poline) {
    setColorSwatch((prev) => ({
      ...prev,
      poline,
    }));
  }

  const getColorForName = (name?: NameFieldValue) => {
    const nameString = (name?.firstName ?? "") + (name?.lastName ?? "");
    return getColorForId(nameString);
  };

  const getColorForId = (id: string) => {
    let color = colorSwatch.colors[id];
    if (!color) {
      const hash = hashString(id);

      if (swatch === "default") {
        color = uclColorsArray[hash % uclColorsArray.length].toLowerCase();
      } else {
        color = poline.colorsCSS[hash % poline.colors.length];
      }

      // Set in jotai for future reference, but always return the computed color
      setColorSwatch((prev) => ({
        ...prev,
        colors: { ...prev.colors, [id]: color },
      }));
    }
    return color;
  };

  return {
    swatch,
    getColorForId,
    getColorForName,
  };
};
